import { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Dashboard_screen.css";
import YakapLogo from "../../Assets/Images/yakap_logo.avif";
import { useNavigate } from "react-router-dom";
import socket from "../../Components/Socket/Socket";

//API calls
import { fetchNewPatients, fetchQueueList, setNowServing, skipQueue } from "../../Helpers/ApiCalls/Dashboard/DashboardApi";
// import { getQueuedPatients } from "../../Components/Helpers/API/QueueApi/QueueApi";

import SidebarMenu from "../../Components/Sidebar/Sidebar";

//images
// import Queued from "../../Assets/Images/queued.png";
// No image placeholders; keep minimal text-only empty state for consistency

import { getName, getRole } from "../../Components/Helpers/Utilities/Common.js";
import { formatDateWithTime } from "../../Helpers/Utils/Common.js";

// Helper function to format queue number with spaces (e.g., "Y001" -> "Y 001")
const formatQueueNumberWithSpaces = (queueNo) => {
  if (!queueNo) return '';
  // Insert space after the letter prefix
  return queueNo.replace(/^([A-Za-z]+)/, '$1 ');
};

function Dashboard() {
  const role = getRole();
  const [timeOfDay, setTimeOfDay] = useState(getTimeOfDay());
  const [data, setData] = useState({ no_of_new_patients_today: 0 });
  const [queueData, setQueueData] = useState([]);
  const [skipData, setSkipData] = useState([]);
  const [current, setCurrent] = useState({});
  const currentDate = new Date();
  const navigate = useNavigate();

  // Get user's lane from localStorage
  const userLane = JSON.parse(localStorage.getItem('lane') || 'null');

  // Socket emit function to send queue number to other clients (e.g., kiosk display)
  const sendQueuePage = (queueData) => {
    socket.emit("message", {
      csr: {
        queueNumber: queueData?.queue_no ? formatQueueNumberWithSpaces(queueData.queue_no) : '',
      }
    });
  };

  // Socket emit function to notify skip action
  const skipQueuePage = () => {
    socket.emit("message", {
      skip: {}
    });
  };

  async function getDashboardData() {
    try {
      const dateToday = new Date().toISOString().split("T")[0];
      const response = await fetchNewPatients([dateToday, dateToday]);
      // API returns { data: { data: count } } - extract the actual count value
      const count = response?.data?.data || response?.data || 0;
      setData(prev => ({ ...prev, no_of_new_patients_today: count }));
    } catch (error) {
      console.error("Error fetching new patients:", error);
    }
  }

  // Fetch queue list - only Y-prefixed queue numbers (Yakap lane)
  async function getQueueList() {
    try {
      const response = await fetchQueueList('yakap');
      if (response?.data) {
        const nowServing = response.data.now_serving || {};
        setCurrent(nowServing);
        localStorage.setItem("queue_id", JSON.stringify(nowServing));
        
        // Filter queue list to only show Y-prefixed queue numbers
        const allQueues = response.data.patient_queues || [];
        const yakapQueues = allQueues.filter(item => 
          item.queue_no && item.queue_no.toUpperCase().startsWith('Y')
        );
        setQueueData(yakapQueues);
        
        // Filter skip queue to only show Y-prefixed queue numbers
        const allSkip = response.data.patient_skip || [];
        const yakapSkip = allSkip.filter(item => 
          item.queue_no && item.queue_no.toUpperCase().startsWith('Y')
        );
        setSkipData(yakapSkip);
      } else {
        setQueueData([]);
        setSkipData([]);
        setCurrent({});
      }
    } catch (error) {
      console.error("Error fetching queue list:", error);
    }
  }

  // Handle Next button - serve next in queue
  async function handleNext() {
    const nextInQueue = queueData[0];
    if (!nextInQueue) return;
    
    try {
      const response = await setNowServing(nextInQueue?.id, current?.id, userLane);
      if (response?.data) {
        getQueueList();
        // Emit socket event to update other clients (e.g., kiosk display)
        sendQueuePage(nextInQueue);
      }
    } catch (error) {
      console.error("Error serving next queue:", error);
    }
  }

  // Handle Skip button - skip current serving
  async function handleSkip() {
    if (!current?.id) return;
    
    try {
      const response = await skipQueue(current.id);
      if (response?.data) {
        getQueueList();
        // Emit socket event to notify skip action
        skipQueuePage();
      }
    } catch (error) {
      console.error("Error skipping queue:", error);
    }
  }

  // Handle adding from skip queue
  function handleAddSkip(item) {
    setCurrent({ ...item, skip: "1" });
    localStorage.setItem("queue_id", JSON.stringify({ ...item, skip: "1" }));
    // Emit socket event to update other clients
    sendQueuePage(item);
  }

  //get greetings by time of the day
  function getTimeOfDay() {
    const currentHour = new Date().getHours();
    if (currentHour >= 5 && currentHour < 12) {
      return "Morning";
    } else if (currentHour >= 12 && currentHour < 18) {
      return "Afternoon";
    } else {
      return "Evening";
    }
  }

  // Socket listener for real-time updates from other clients (e.g., kiosk)
  useEffect(() => {
    socket.on("message", (data) => {
      // Refresh queue list when kiosk sends update
      if (data.kiosk) {
        getQueueList();
      }
      // Handle post_consultation events if needed
      if (data.post_consultation) {
        getQueueList();
      }
    });

    // Cleanup on unmount
    return () => {
      socket.off("message");
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for localStorage changes (when queue_id is removed after Proceed/No)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "queue_id" && e.newValue === null) {
        // queue_id was removed, refresh queue list
        getQueueList();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    
    // Also poll for changes in the same tab (storage event doesn't fire in same tab)
    const pollInterval = setInterval(() => {
      const queueIdStr = localStorage.getItem("queue_id");
      // If queue_id was removed and we have a current queue, refresh
      if (!queueIdStr && current?.queue_no) {
        getQueueList();
      }
    }, 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(pollInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.queue_no]);

  useEffect(() => {
    getDashboardData();
    getQueueList();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeOfDay(getTimeOfDay());
    }, 60000); // Update every minute to reflect the current time of day

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="App dashboard">
      <SidebarMenu active={"Dashboard"} />
      <div className=" ml-2 mt-3 col outer-dashboard">
        <Row className="ps-4 mb-4 greeting-dashboard">
          <Col xs={10} className="user-info">
            <Row className="font-big title-2">
              Good {timeOfDay},{" "}
              {(role === "director" || role === "pcp" || role === "specialist") ? "" : "nurse"}{" "}
              {getName()}👋!
            </Row>
          </Col>
          <Col>
            <Row className="pt-3">
              <h6 id="normal-text account-owner">
                {formatDateWithTime(currentDate)}
              </h6>
            </Row>
          </Col>
        </Row>
        <Row className="dashboard-main">
          <Col xs={12} lg={7} className="left-pane p-3">
            <div className="left-actions">
              <div className="left-top">
                <div className="card yakap-card-card new-patients-card">
                  <div className="yakap-card-header">
                    <p className="yakap-subtitle">New Patients</p>
                  </div>
                  <div className="yakap-card-body">
                    <p className="new-patient-count">{data.no_of_new_patients_today || 0}</p>
                  </div>
                </div>
              </div>
              <div className="left-buttons-wrap">
              <div className="left-buttons">
                <button
                  className="action-lg-btn register-btn"
                  disabled={!current?.queue_no}
                  onClick={() => navigate("/philhealth/is-registered")}
                >
                  Register Patient
                </button>
                <button
                  className="action-lg-btn consult-btn"
                  disabled={!current?.queue_no}
                  onClick={() =>
                    navigate("/consult/select-patient", {
                      state: {
                        queueNo: current?.queue_no,
                        queueId: current?.id,
                        patient_id: current?.patient_id,
                        master_patient_id: current?.master_patient_id,
                      },
                    })
                  }
                >
                  Consult Patient
                </button>
              </div>
              </div>
              <div className="yakap-logo-cont">
                <img src={YakapLogo} alt="Yakap logo" className="yakap-logo" />
              </div>
            </div>
          </Col>
          <Col xs={12} lg={5} className="right-pane">
            <Row>
              <Col xs={12} md={7} className="p-3">
                <div className="card yakap-card now-serving-card">
                  <div className="yakap-card-header">
                    <p className="yakap-title centered">Now Serving</p>
                  </div>
                  <div className="yakap-card-body">
                    <p className="now-serving-digit">{current?.queue_no || "--"}</p>
                  </div>
                  <div className="yakap-card-footer">
                    <button 
                      className={`btn-skip ${current?.queue_no && current?.skip !== "1" ? "btn-skip-active" : ""}`} 
                      disabled={!current?.id || current?.skip === "1"}
                      onClick={handleSkip}
                    >Skip</button>
                    <button 
                      className="btn-next" 
                      disabled={queueData.length === 0 || current?.queue_no}
                      onClick={handleNext}
                    >Next</button>
                  </div>
                </div>
              </Col>
              <Col xs={12} md={5} className="p-3">
                <div className="card yakap-card queue-list-card">
                  <div className="yakap-card-header">
                    <p className="yakap-subtitle">Queue List</p>
                  </div>
                  <div className="yakap-card-body">
                    {queueData.length > 0 ? (
                      <ul className="queue-list-items">
                        {queueData.map((item, index) => (
                          <li key={item.id || index}>{item.queue_no}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="dashboard-empty">
                        <span>No data</span>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              <Col xs={12} className="p-3">
                <div className="card yakap-card skip-queue-card">
                  <div className="yakap-card-header">
                    <p className="yakap-subtitle">Skip Queue</p>
                  </div>
                  <div className="yakap-card-body">
                {skipData.length > 0 ? (
                  <>
                    <div className="skip-top-row">
                      <span className="skip-current">
                        {skipData[0]?.queue_no || ""}
                      </span>
                      <button
                        className="btn-add-skip"
                        onClick={() => handleAddSkip(skipData[0])}
                      >
                        +
                      </button>
                    </div>
                    {skipData.length > 1 && (
                      <ul className="skip-list-items">
                        {skipData.slice(1).map((item, index) => (
                          <li key={item.id || index} className="skip-item">
                            <span>{item.queue_no}</span>
                            <button
                              className="btn-add-skip"
                              onClick={() => handleAddSkip(item)}
                            >
                              +
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                      <div className="dashboard-empty">
                        <span>No data</span>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default Dashboard;
